import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Chip, 
  Avatar, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio
} from '@mui/material';
import { People as UsersIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, CheckCircle as ApproveIcon, Cancel as RejectIcon, Archive as ArchiveIcon, Unarchive as UnarchiveIcon, LockReset as ResetPasswordIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { lt } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl, API_ENDPOINTS } from '../config/api';
import { useTranslation } from 'react-i18next';

interface PermissionTemplate {
  id: string;
  templateCode: string;
  templateName: string;
  description?: string;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role?: string; // DEPRECATED: Use templates instead
  templates?: PermissionTemplate[]; // Permission templates assigned to user
  department?: string;
  position?: string;
  employeeId?: string;
  approvalStatus: string;
  lastLogin?: string;
  createdAt: string;
  isActive?: boolean;
  emailVerified?: boolean;
  pointsBalance?: number;
  workPhone?: string;
  phoneNumber?: string;
  contactsVisible?: boolean;
  etatas?: number;
  employmentStartDate?: string;
  canApproveSchedules?: boolean;
  workAccountingType?: 'normal' | 'summary';
}

interface EditUserForm {
  firstName: string;
  lastName: string;
  email: string;
  role?: string; // DEPRECATED: Optional - use permission templates instead
  department: string;
  position: string;
  employeeId: string;
  approvalStatus: string;
  isActive: boolean;
  workPhone: string;
  phoneNumber: string;
  etatas: number;
  employmentStartDate?: string;
  etatasEffectiveMonth?: string; // YYYY-MM
  canApproveSchedules: boolean;
  workAccountingType: 'normal' | 'summary';
}

const Users: React.FC = () => {
  const { user, token, hasPermission } = useAuth();
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false); // Hide archived users by default
  const [selectedDepartment, setSelectedDepartment] = useState<string>(''); // Empty string = all departments
  const [approveDialog, setApproveDialog] = useState<{ open: boolean; user: User | null }>({ open: false, user: null });
  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; user: User | null }>({ open: false, user: null });
  const [editDialog, setEditDialog] = useState<{ open: boolean; user: User | null }>({ open: false, user: null });
  const [archiveDialog, setArchiveDialog] = useState<{ open: boolean; user: User | null; action: 'archive' | 'unarchive' }>({ 
    open: false, 
    user: null,
    action: 'archive'
  });
  const [resetPasswordDialog, setResetPasswordDialog] = useState<{ open: boolean; user: User | null }>({ 
    open: false, 
    user: null
  });
  const [newPassword, setNewPassword] = useState('');
  const [editForm, setEditForm] = useState<EditUserForm>({
    firstName: '',
    lastName: '',
    email: '',
    role: 'employee',
    department: '',
    position: '',
    employeeId: '',
    approvalStatus: 'pending',
    isActive: true,
    workPhone: '',
    phoneNumber: '',
    etatas: 0,
    employmentStartDate: undefined,
    etatasEffectiveMonth: '',
    canApproveSchedules: false,
    workAccountingType: 'normal',
  });
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchUsers();
    fetchPendingUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.USERS), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.error || t('users.failedToFetchUsers') });
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setMessage({ type: 'error', text: t('users.failedToFetchUsers') });
    }
  };

  const fetchPendingUsers = async () => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.PENDING_USERS), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPendingUsers(data.users);
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.error || t('users.failedToFetchPendingUsers') });
      }
    } catch (error) {
      console.error('Error fetching pending users:', error);
      setMessage({ type: 'error', text: t('users.failedToFetchPendingUsers') });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!approveDialog.user) return;
    
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.APPROVE_USER(approveDialog.user.id)), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ approvedBy: user?.id })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: t('users.userApprovedSuccessfully') });
        fetchUsers();
        fetchPendingUsers();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.error || 'Failed to approve user' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to approve user' });
    }
    setApproveDialog({ open: false, user: null });
  };

  const handleReject = async () => {
    if (!rejectDialog.user) return;
    
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.REJECT_USER(rejectDialog.user.id)), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ reason: 'Rejected by admin' })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: t('users.userRejectedSuccessfully') });
        fetchUsers();
        fetchPendingUsers();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.error || 'Failed to reject user' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to reject user' });
    }
    setRejectDialog({ open: false, user: null });
  };

  const handleArchiveUser = async () => {
    if (!archiveDialog.user) return;
    
    const isArchiving = archiveDialog.action === 'archive';
    
    try {
      const response = await fetch(
        `${getApiUrl(API_ENDPOINTS.USERS)}/${archiveDialog.user.id}/archive`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ isActive: !isArchiving })
        }
      );

      if (response.ok) {
        setMessage({ 
          type: 'success', 
          text: isArchiving ? 'User archived successfully' : 'User unarchived successfully' 
        });
        fetchUsers();
        setArchiveDialog({ open: false, user: null, action: 'archive' });
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.error || 'Failed to archive user' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to archive user' });
    }
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const handleResetPassword = async () => {
    if (!resetPasswordDialog.user || !newPassword) return;
    
    try {
      const response = await fetch(
        `${getApiUrl(API_ENDPOINTS.USERS)}/${resetPasswordDialog.user.id}/reset-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ newPassword })
        }
      );

      if (response.ok) {
        setMessage({ 
          type: 'success', 
          text: `Password reset successfully for ${resetPasswordDialog.user.firstName} ${resetPasswordDialog.user.lastName}. Temporary password: ${newPassword}. User must change password on next login.`
        });
        setResetPasswordDialog({ open: false, user: null });
        setNewPassword('');
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.error || 'Failed to reset password' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to reset password' });
    }
  };

  const handleEdit = (userToEdit: User) => {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    setEditForm({
      firstName: userToEdit.firstName,
      lastName: userToEdit.lastName,
      email: userToEdit.email,
      role: userToEdit.role || '', // DEPRECATED: Optional - use permission templates instead
      department: userToEdit.department || '',
      position: userToEdit.position || '',
      employeeId: userToEdit.employeeId || '',
      approvalStatus: userToEdit.approvalStatus,
      isActive: userToEdit.isActive || true,
      workPhone: userToEdit.workPhone || '',
      phoneNumber: userToEdit.phoneNumber || '',
      etatas: userToEdit.etatas || 0,
      employmentStartDate: userToEdit.employmentStartDate || '',
      etatasEffectiveMonth: currentMonth,
      canApproveSchedules: userToEdit.canApproveSchedules || false,
      workAccountingType: userToEdit.workAccountingType || 'normal',
    });
    setEditDialog({ open: true, user: userToEdit });
  };

  const canEditDepartment = hasPermission('*.*');

  const handleSaveEdit = async () => {
    if (!editDialog.user) return;

    try {
      const { role: _role, department: _department, ...formWithoutRole } = editForm;
      const payload = canEditDepartment
        ? { ...formWithoutRole, department: editForm.department }
        : formWithoutRole;
      const updateUrl = getApiUrl(API_ENDPOINTS.UPDATE_USER(editDialog.user.id));
      const response = await fetch(updateUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: t('users.userUpdatedSuccessfully') });
        fetchUsers();
        fetchPendingUsers();
        setEditDialog({ open: false, user: null });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.error || 'Failed to update user' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update user' });
    }
  };

  const getRoleColor = (role: string) => {
    const baseRole = role?.replace(/_f$/, '') || role;
    switch (baseRole) {
      case 'admin': return 'error';
      case 'manager': return 'warning';
      case 'employee': return 'primary';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  // Infer likely female from Lithuanian first name (names ending in -a, -ė; not -as, -is, -us)
  const isLikelyFemale = (firstName: string): boolean => {
    if (!firstName || typeof firstName !== 'string') return false;
    const trimmed = firstName.trim();
    if (trimmed.length < 2) return false;
    const lastTwo = trimmed.slice(-2);
    const lastOne = trimmed.slice(-1);
    if (lastTwo === 'as' || lastTwo === 'is' || lastTwo === 'us') return false;
    return lastOne === 'a' || lastOne === 'ė';
  };

  // Helper function to translate role values (gender-aware for Lithuanian)
  const translateRole = (role: string, firstName?: string) => {
    const useFemale = role?.endsWith('_f') || (firstName ? isLikelyFemale(firstName) : false);
    const baseRole = role?.replace(/_f$/, '') || role;
    switch (baseRole) {
      case 'admin': return useFemale ? t('users.adminFemale') : t('users.admin');
      case 'manager': return useFemale ? t('users.managerFemale') : t('users.manager');
      case 'employee': return useFemale ? t('users.employeeFemale') : t('users.employee');
      default: return role;
    }
  };

  // Helper function to translate status values
  const translateStatus = (status: string) => {
    switch (status) {
      case 'approved': return t('users.approved');
      case 'pending': return t('users.pending');
      case 'rejected': return t('users.rejected');
      case 'active': return t('users.active');
      case 'inactive': return t('users.inactive');
      default: return status;
    }
  };

  // Extract unique departments from users
  const getUniqueDepartments = (): string[] => {
    const departments = users
      .map(user => user.department)
      .filter((dept): dept is string => Boolean(dept && dept.trim()))
      .filter((dept, index, self) => self.indexOf(dept) === index) // Get unique values
      .sort();
    return departments;
  };

  // Filter users based on selected filters
  const filteredUsers = users.filter((user: User) => {
    // Filter by archived status
    if (!showArchived && user.isActive === false) {
      return false;
    }
    
    // Filter by department
    if (selectedDepartment && user.department !== selectedDepartment) {
      return false;
    }
    
    return true;
  });

  // Current user's row always first; preserves order for others (pinning would apply to 2nd, 3rd, etc.)
  const sortedFilteredUsers = useMemo(() => {
    if (!user?.id) return filteredUsers;
    const currentUserRow = filteredUsers.find((u) => u.id === user.id);
    const others = filteredUsers.filter((u) => u.id !== user.id);
    return currentUserRow ? [currentUserRow, ...others] : filteredUsers;
  }, [filteredUsers, user?.id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>{t('users.loadingUsers')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t('users.usersManagement')}
        </Typography>
      </Box>

      {message.text && (
        <Alert 
          severity={message.type as any} 
          sx={{ mb: 3 }}
          onClose={() => setMessage({ type: '', text: '' })}
        >
          {message.text}
        </Alert>
      )}

      {/* Pending Approvals Section */}
      {pendingUsers.length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3, color: 'warning.main' }}>
              {t('users.pendingApprovals')} ({pendingUsers.length})
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('users.user')}</TableCell>
                    <TableCell>{t('users.role')}</TableCell>
                    <TableCell>{t('users.department')}</TableCell>
                    <TableCell>{t('users.status')}</TableCell>
                    <TableCell>{t('users.isActive')}</TableCell>
                    <TableCell>{t('users.emailVerified')}</TableCell>
                    <TableCell>{t('users.pointsBalance')}</TableCell>
                    <TableCell>{t('users.workPhone')}</TableCell>
                    <TableCell>{t('users.etatas')}</TableCell>
                    <TableCell>{t('users.registrationDate')}</TableCell>
                    <TableCell align="center">{t('users.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingUsers.map((pendingUser) => (
                    <TableRow key={pendingUser.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ width: 40, height: 40 }}>
                            {pendingUser.firstName?.[0]}{pendingUser.lastName?.[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {pendingUser.firstName} {pendingUser.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {pendingUser.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {pendingUser.templates && pendingUser.templates.length > 0 ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {pendingUser.templates.map((template) => (
                              <Chip
                                key={template.id}
                                label={template.templateName}
                                color="primary"
                                size="small"
                              />
                            ))}
                          </Box>
                        ) : pendingUser.role ? (
                          <Chip
                            label={translateRole(pendingUser.role, pendingUser.firstName) + ' (Deprecated)'}
                            color={getRoleColor(pendingUser.role) as any}
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t('users.noTemplates', 'Nėra šablonų')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {pendingUser.department || t('users.notAvailable')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={translateStatus(pendingUser.approvalStatus)}
                          color={getStatusColor(pendingUser.approvalStatus) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={pendingUser.isActive ? t('users.active') : t('users.inactive')}
                          color={pendingUser.isActive ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={pendingUser.emailVerified ? t('users.verified') : t('users.notVerified')}
                          color={pendingUser.emailVerified ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {pendingUser.pointsBalance !== undefined ? parseFloat(pendingUser.pointsBalance.toString()).toFixed(2) : '0.00'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {pendingUser.workPhone || t('users.notAvailable')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {pendingUser.etatas !== undefined ? parseFloat(pendingUser.etatas.toString()).toFixed(2) : '0.00'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(pendingUser.createdAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<ApproveIcon />}
                            onClick={() => setApproveDialog({ open: true, user: pendingUser })}
                          >
                            {t('users.approve')}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<RejectIcon />}
                            onClick={() => setRejectDialog({ open: true, user: pendingUser })}
                          >
                            {t('users.reject')}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => handleEdit(pendingUser)}
                          >
                            {t('users.edit')}
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* All Users Section */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3 }}>
            {t('users.allUsers')}
          </Typography>

          {/* Filter Section */}
          <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showArchived}
                    onChange={(e) => setShowArchived(e.target.checked)}
                  />
                }
                label="Rodyti archyvuotus vartotojus"
              />
              
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Filtruoti pagal skyrių</InputLabel>
                <Select
                  value={selectedDepartment}
                  label="Filtruoti pagal skyrių"
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                >
                  <MenuItem value="">Visi skyriai</MenuItem>
                  {getUniqueDepartments().map((dept: string) => (
                    <MenuItem key={dept} value={dept}>
                      {dept}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <Typography variant="body2" color="text.secondary">
              Rodoma {filteredUsers.length} iš {users.length} vartotojų
            </Typography>
          </Box>

          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              minHeight: {
                xs: 'max(calc(100vh - 280px), 250px)',
                ...(selectedDepartment && { sm: 400 }),
              },
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('users.user')}</TableCell>
                  <TableCell>{t('users.templates', 'Šablonai')}</TableCell>
                  <TableCell>{t('users.department')}</TableCell>
                  <TableCell>{t('users.status')}</TableCell>
                  <TableCell>{t('users.isActive')}</TableCell>
                  <TableCell>{t('users.emailVerified')}</TableCell>
                  <TableCell>{t('users.pointsBalance')}</TableCell>
                  <TableCell>{t('users.workPhone')}</TableCell>
                  <TableCell>{t('users.etatas')}</TableCell>
                  <TableCell>{t('users.lastLogin')}</TableCell>
                  <TableCell align="center">{t('users.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedFilteredUsers.map((userItem) => (
                  <TableRow key={userItem.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ width: 40, height: 40 }}>
                          {userItem.firstName?.[0]}{userItem.lastName?.[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {userItem.firstName} {userItem.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {userItem.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {userItem.templates && userItem.templates.length > 0 ? (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {userItem.templates.map((template) => (
                            <Chip
                              key={template.id}
                              label={template.templateName}
                              color="primary"
                              size="small"
                            />
                          ))}
                        </Box>
                      ) : userItem.role ? (
                        <Chip
                          label={translateRole(userItem.role, userItem.firstName) + ' (Deprecated)'}
                          color={getRoleColor(userItem.role) as any}
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('users.noTemplates', 'Nėra šablonų')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {userItem.department || t('users.notAvailable')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={translateStatus(userItem.approvalStatus)}
                        color={getStatusColor(userItem.approvalStatus) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={userItem.isActive ? t('users.active') : t('users.inactive')}
                        color={userItem.isActive ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={userItem.emailVerified ? t('users.verified') : t('users.notVerified')}
                        color={userItem.emailVerified ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {userItem.pointsBalance !== undefined ? parseFloat(userItem.pointsBalance.toString()).toFixed(2) : '0.00'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {userItem.workPhone || t('users.notAvailable')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {userItem.etatas !== undefined ? parseFloat(userItem.etatas.toString()).toFixed(2) : '0.00'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {userItem.lastLogin ? new Date(userItem.lastLogin).toLocaleDateString() : t('users.never')}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          startIcon={<EditIcon />}
                          onClick={() => handleEdit(userItem)}
                        >
                          {t('users.edit')}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          startIcon={<ResetPasswordIcon />}
                          onClick={() => setResetPasswordDialog({ open: true, user: userItem })}
                        >
                          Atstatyti slaptažodį
                        </Button>
                        {userItem.isActive ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<ArchiveIcon />}
                            onClick={() => setArchiveDialog({ open: true, user: userItem, action: 'archive' })}
                          >
                            Archyvuoti
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            startIcon={<UnarchiveIcon />}
                            onClick={() => setArchiveDialog({ open: true, user: userItem, action: 'unarchive' })}
                          >
                            Atstatyti
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Approve Dialog */}
      <Dialog open={approveDialog.open} onClose={() => setApproveDialog({ open: false, user: null })}>
        <DialogTitle>{t('users.approveUser')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('users.approveUserConfirm', { firstName: approveDialog.user?.firstName, lastName: approveDialog.user?.lastName })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('users.approveUserDescription')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog({ open: false, user: null })}>{t('users.cancel')}</Button>
          <Button onClick={handleApprove} variant="contained" color="success">{t('users.approve')}</Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, user: null })}>
        <DialogTitle>{t('users.rejectUser')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('users.rejectUserConfirm', { firstName: rejectDialog.user?.firstName, lastName: rejectDialog.user?.lastName })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('users.rejectUserDescription')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, user: null })}>{t('users.cancel')}</Button>
          <Button onClick={handleReject} variant="contained" color="error">{t('users.reject')}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog 
        open={editDialog.open} 
        onClose={() => setEditDialog({ open: false, user: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('users.editUser')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                fullWidth
                label={t('users.firstName')}
                value={editForm.firstName}
                onChange={(e) => setEditForm(prev => ({ ...prev, firstName: e.target.value }))}
                margin="normal"
              />
              <TextField
                fullWidth
                label={t('users.lastName')}
                value={editForm.lastName}
                onChange={(e) => setEditForm(prev => ({ ...prev, lastName: e.target.value }))}
                margin="normal"
              />
              <TextField
                fullWidth
                label={t('users.email')}
                value={editForm.email}
                onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                margin="normal"
                type="email"
                sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>{t('users.approvalStatus')}</InputLabel>
                <Select
                  value={editForm.approvalStatus}
                  label={t('users.approvalStatus')}
                  onChange={(e) => setEditForm(prev => ({ ...prev, approvalStatus: e.target.value }))}
                >
                  <MenuItem value="pending">{t('users.pending')}</MenuItem>
                  <MenuItem value="approved">{t('users.approved')}</MenuItem>
                  <MenuItem value="rejected">{t('users.rejected')}</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label={t('users.department')}
                value={editForm.department}
                onChange={(e) => setEditForm(prev => ({ ...prev, department: e.target.value }))}
                margin="normal"
                disabled={!canEditDepartment}
                helperText={!canEditDepartment ? t('users.departmentSuperAdminOnly', 'Only super admins can change department') : undefined}
              />
              <TextField
                fullWidth
                label={t('users.position')}
                value={editForm.position}
                onChange={(e) => setEditForm(prev => ({ ...prev, position: e.target.value }))}
                margin="normal"
              />
              <TextField
                fullWidth
                label={t('users.employeeId')}
                value={editForm.employeeId}
                onChange={(e) => setEditForm(prev => ({ ...prev, employeeId: e.target.value }))}
                margin="normal"
                sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>{t('users.isActive')}</InputLabel>
                <Select
                  value={editForm.isActive ? 'true' : 'false'}
                  label={t('users.isActive')}
                  onChange={(e) => setEditForm(prev => ({ ...prev, isActive: e.target.value === 'true' }))}
                >
                  <MenuItem value="true">{t('users.active')}</MenuItem>
                  <MenuItem value="false">{t('users.inactive')}</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label={t('users.workPhone')}
                value={editForm.workPhone}
                onChange={(e) => setEditForm(prev => ({ ...prev, workPhone: e.target.value }))}
                margin="normal"
              />
              <TextField
                fullWidth
                label={t('users.phoneNumber')}
                value={editForm.phoneNumber}
                onChange={(e) => setEditForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
                margin="normal"
              />
              <TextField
                fullWidth
                label={t('users.employmentStartDate', 'Employment start date')}
                value={editForm.employmentStartDate || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, employmentStartDate: e.target.value }))}
                margin="normal"
                type="date"
                InputLabelProps={{ shrink: true }}
                helperText={t('users.employmentStartDateHelp', 'Data, nuo kurios pradedamas darbas')}
              />
              <TextField
                fullWidth
                label={t('users.etatas')}
                value={editForm.etatas}
                onChange={(e) => setEditForm(prev => ({ ...prev, etatas: parseFloat(e.target.value) || 0 }))}
                margin="normal"
                type="number"
                inputProps={{ min: 0, max: 1, step: 0.01 }}
                helperText={t('users.etatasHelp', 'Employment fraction from 0 to 1 (e.g., 0.75)')}
              />
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={lt}>
                <DatePicker
                  label={t('users.etatasEffectiveMonth', 'Etatas change effective from (month)')}
                  value={editForm.etatasEffectiveMonth ? (() => {
                    const [y, m] = editForm.etatasEffectiveMonth!.split('-').map(Number);
                    return new Date(y, m - 1, 1);
                  })() : null}
                  onChange={(date: Date | null) => {
                    if (!date) {
                      setEditForm(prev => ({ ...prev, etatasEffectiveMonth: '' }));
                      return;
                    }
                    const y = date.getFullYear();
                    const m = String(date.getMonth() + 1).padStart(2, '0');
                    setEditForm(prev => ({ ...prev, etatasEffectiveMonth: `${y}-${m}` }));
                  }}
                  views={['year', 'month']}
                  slotProps={{ textField: { fullWidth: true, margin: 'normal', helperText: t('users.etatasEffectiveMonthHelp', 'Work schedules will use this etatas from the selected month onward; previous months keep the previous value.') } }}
                />
              </LocalizationProvider>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editForm.canApproveSchedules}
                    onChange={(e) => setEditForm(prev => ({ ...prev, canApproveSchedules: e.target.checked }))}
                  />
                }
                label={t('users.canApproveSchedules', 'Darbuotojų grafikų tvirtinimas')}
                sx={{ mt: 2 }}
              />
              <FormControl fullWidth margin="normal" sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                  {t('users.workAccountingType', 'Darbo apskaitos tipas')}
                </Typography>
                <RadioGroup
                  row
                  value={editForm.workAccountingType}
                  onChange={(e) => setEditForm(prev => ({ ...prev, workAccountingType: e.target.value as 'normal' | 'summary' }))}
                >
                  <FormControlLabel 
                    value="normal" 
                    control={<Radio />} 
                    label={t('users.normalAccounting', 'Įprasta darbo apskaita')} 
                  />
                  <FormControlLabel 
                    value="summary" 
                    control={<Radio />} 
                    label={t('users.summaryAccounting', 'Suminė darbo apskaita')} 
                  />
                </RadioGroup>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {t('users.workAccountingTypeHelp', 'Pasirinkite kaip skaičiuojamos reikalingos mėnesio valandos')}
                </Typography>
              </FormControl>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, user: null })}>{t('users.cancel')}</Button>
          <Button onClick={handleSaveEdit} variant="contained" color="primary">{t('users.saveChanges')}</Button>
        </DialogActions>
      </Dialog>

      {/* Archive Dialog */}
      <Dialog open={archiveDialog.open} onClose={() => setArchiveDialog({ open: false, user: null, action: 'archive' })}>
        <DialogTitle>
          {archiveDialog.action === 'archive' ? 'Archyvuoti vartotoją' : 'Atstatyti vartotoją'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {archiveDialog.action === 'archive' 
              ? `Ar tikrai norite archyvuoti vartotoją ${archiveDialog.user?.firstName} ${archiveDialog.user?.lastName}? Vartotojas negalės prisijungti ir nebus matomas darbo grafike.`
              : `Ar tikrai norite atstatyti vartotoją ${archiveDialog.user?.firstName} ${archiveDialog.user?.lastName}? Vartotojas galės vėl prisijungti ir bus matomas darbo grafike.`
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveDialog({ open: false, user: null, action: 'archive' })}>
            Atšaukti
          </Button>
          <Button 
            onClick={handleArchiveUser} 
            color={archiveDialog.action === 'archive' ? 'warning' : 'success'}
            variant="contained"
          >
            {archiveDialog.action === 'archive' ? 'Archyvuoti' : 'Atstatyti'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialog.open} onClose={() => setResetPasswordDialog({ open: false, user: null })}>
        <DialogTitle>
          Atstatyti slaptažodį
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Atstatyti slaptažodį vartotojui: <strong>{resetPasswordDialog.user?.firstName} {resetPasswordDialog.user?.lastName}</strong>
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Vartotojas privalės pakeisti slaptažodį šiandien po prisijungimo.
          </Alert>
          <TextField
            fullWidth
            label="Naujas slaptažodis"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            margin="normal"
            type="text"
            helperText="Minimalus ilgis: 8 simboliai"
          />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setNewPassword(generateRandomPassword())}
            >
              Sugeneruoti saugų slaptažodį
            </Button>
            {newPassword && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  navigator.clipboard.writeText(newPassword);
                }}
              >
                Kopijuoti slaptažodį
              </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setResetPasswordDialog({ open: false, user: null });
            setNewPassword('');
          }}>
            Atšaukti
          </Button>
          <Button 
            onClick={handleResetPassword} 
            color="secondary"
            variant="contained"
            disabled={!newPassword || newPassword.length < 8}
          >
            Atstatyti slaptažodį
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users; 